import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Spin, Alert, Space } from 'antd';
import { ArrowLeftOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { PreJoin } from '@livekit/components-react';
import '@livekit/components-styles';
import { useAuth } from '../../context/AuthContext';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import VideoCallRoom from './VideoCallRoom';
import CreditsBalanceTag from './CreditsBalanceTag';
import PieceBackdrop from '../Skargard/PieceBackdrop';
import Fold from '../Skargard/Fold';
import './VideoCall.css';

const { Title, Text } = Typography;
const BREAKOUT_POLL_MS = 12000;

function EntityCall() {
    const { entityId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const api = useApi();
    const { featureHangoutFacilitator } = useNodeInfo();

    const mainRoomName = `entity-${entityId}`;
    const participantIdentity = user?.username || user?.email || user?.id || 'anonymous';

    const [entityName, setEntityName] = useState(null);
    const [userChoices, setUserChoices] = useState(null);
    // null = in the main room; otherwise the breakout room name the participant
    // switched into (client-driven "move" — see pods/hangouts/service.py's
    // move_breakout_participant docstring for why this isn't server-driven).
    const [breakoutAssignment, setBreakoutAssignment] = useState(null); // { breakoutId, roomName, facilitationPrompt } | null
    const [activeRoomName, setActiveRoomName] = useState(mainRoomName);

    useEffect(() => {
        api.get(`/api/entities/${entityId}`)
            .then(data => { if (data?.name) setEntityName(data.name); })
            .catch(() => {});
    }, [entityId]);

    const checkBreakout = useCallback(() => {
        if (!featureHangoutFacilitator || !user?.id) return;
        api.get(`/api/hangouts/${entityId}/breakout`, { params: { user_id: user.id } })
            .then(breakout => api.get(`/api/hangouts/breakout/${breakout.id}/my-room`, { params: { user_id: user.id } })
                .then(myRoom => {
                    if (myRoom.room_name) {
                        setBreakoutAssignment({
                            breakoutId: breakout.id,
                            roomName: myRoom.room_name,
                            facilitationPrompt: myRoom.facilitation_prompt,
                        });
                    } else {
                        setBreakoutAssignment(null);
                    }
                }))
            .catch(() => setBreakoutAssignment(null));
    }, [api, entityId, user?.id, featureHangoutFacilitator]);

    useEffect(() => {
        if (!userChoices) return; // only poll once actually in the call
        checkBreakout();
        const id = setInterval(checkBreakout, BREAKOUT_POLL_MS);
        return () => clearInterval(id);
    }, [userChoices, checkBreakout]);

    // If the breakout ends (or this participant's assignment disappears) while
    // they're still in it, fall back to the main room automatically.
    useEffect(() => {
        if (!breakoutAssignment && activeRoomName !== mainRoomName) {
            setActiveRoomName(mainRoomName);
        }
    }, [breakoutAssignment, activeRoomName, mainRoomName]);

    // Hanging up an actual call returns you to the entity you were called on —
    // that is the thing you were just doing something with.
    const handleLeave = () => navigate(`/entity/${entityId}`);
    // Backing out of the waiting room is different: you never joined, so there
    // is nothing to go "back" to in the call sense. Home is the safer landing
    // spot — the entity may be one you reached from a link or a notification
    // and have no further business with.
    const handleExitWaitingRoom = () => navigate('/');

    if (userChoices) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-primary)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setUserChoices(null)}>
                        Back to waiting room
                    </Button>
                    {entityName && <Text type="secondary" style={{ fontSize: 13 }}>{entityName}</Text>}
                    {activeRoomName !== mainRoomName && <Text type="secondary" style={{ fontSize: 13 }}>· Breakout room</Text>}
                </div>
                {breakoutAssignment && (
                    <Alert
                        type="info"
                        showIcon
                        icon={<ThunderboltOutlined />}
                        style={{ borderRadius: 0 }}
                        message={
                            activeRoomName === breakoutAssignment.roomName
                                ? (breakoutAssignment.facilitationPrompt || 'You are in a breakout room.')
                                : "You've been assigned to a breakout room."
                        }
                        action={
                            <Space>
                                {activeRoomName !== breakoutAssignment.roomName ? (
                                    <Button size="small" type="primary" onClick={() => setActiveRoomName(breakoutAssignment.roomName)}>
                                        Join breakout room
                                    </Button>
                                ) : (
                                    <Button size="small" onClick={() => setActiveRoomName(mainRoomName)}>
                                        Back to main room
                                    </Button>
                                )}
                            </Space>
                        }
                    />
                )}
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <VideoCallRoom
                        roomName={activeRoomName}
                        participantIdentity={userChoices.username || participantIdentity}
                        entityId={entityId}
                        onLeave={handleLeave}
                        userChoices={userChoices}
                        fullscreen
                    />
                </div>
            </div>
        );
    }

    // The waiting room, and only the waiting room: a field running behind a
    // live video grid would fight the call for attention (and for GPU). The
    // piece hard-clears every frame (ink.persistence is 0) rather than
    // trailing like the claim page's, and its motion is the whole effect —
    // displace 0.3 with twist -1.65 and spin 0.55 turns chapter 04's parallel
    // bars into something that folds through itself while you wait.
    //
    // It states no ground of its own, so it paints on --art-paper and the
    // fixed canvas is the page background rather than a sheet over it.
    return (
        <PieceBackdrop piece={Fold}>
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border-color)', flexShrink: 0 }}>
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleExitWaitingRoom}>
                        Go back
                    </Button>
                    <div style={{ marginLeft: 'auto' }}><CreditsBalanceTag /></div>
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 24, padding: 32 }}>
                    <div style={{ textAlign: 'center' }}>
                        {entityName
                            ? <Title level={4} style={{ margin: 0 }}>{entityName}</Title>
                            : <Spin size="small" />
                        }
                        <Text type="secondary" style={{ fontSize: 13 }}>Set up your camera and microphone before joining</Text>
                    </div>
                    <div className="prejoin-wrapper" data-lk-theme="default">
                        <PreJoin
                            defaults={{ username: participantIdentity, videoEnabled: true, audioEnabled: true }}
                            onSubmit={setUserChoices}
                            onError={err => console.error('PreJoin error:', err)}
                            persistUserChoices
                        />
                    </div>
                </div>
            </div>
        </PieceBackdrop>
    );
}

export default EntityCall;
